import type { SectionsSearchFormColumnsGroup } from '@autobid/strapi-integration/rest/components'
import type { Chip, Update } from '../../types/Search'
import { useDictionary } from '@autobid/ui/composables/useDictionary'
import { useUpdateQuery } from '@autobid/ui/composables/useUpdateQuery'
import { CUSTOM_CHECKBOX_ID } from '@autobid/ui/constants/CUSTOM_CHECKBOX_ID'
import { VEHICLE_TYPE_KEY } from '@autobid/ui/constants/VEHICLE_TYPE_KEY'
import { VEHICLE_TYPE_TO_CATEGORY_MAP } from '@autobid/ui/constants/VEHICLE_TYPE_TO_CATEGORY_MAP'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

interface FilterCurrentValueByTypeParams {
  type: string
  dictionaryKey: string
  storeKey: string
}

const ITEMS_TO_UPDATE_ON_TYPE_CHANGE = [
  { storeKey: 'categoryId-category', dictionaryKey: 'category' },
  { storeKey: 'brandId-brand', dictionaryKey: 'brand' },
]

export const useSearchStore = defineStore('searchStore', () => {
  const router = useRouter()
  const debounced = ref(false)
  const route = useRoute()
  const { updateQuery } = useUpdateQuery()
  const initialQuery = ref({} as Record<string, string>)
  const allDisplayingForms = ref<NonNullable<SectionsSearchFormColumnsGroup['forms']>>([])
  const chips = computed<Chip[]>(() => {
    const getQueryValue = (key: string) => {
      const value = route.query[key]

      if (Array.isArray(value)) {
        const values = value.filter(item => item !== null)
        return values.length ? values : undefined
      }

      return value ?? undefined
    }

    return allDisplayingForms.value.flatMap((form) => {
      const formSchema = Array.isArray(form.formSchema) ? form.formSchema : []
      const title = form.title
        ?? formSchema.map(item => Object.values(item)[0]).join('-')

      return formSchema.flatMap((item) => {
        const parsedChips: Chip[] = []
        const addQueryChips = (keys: string | string[]) => {
          const queryKeys = Array.isArray(keys) ? keys : [keys]

          queryKeys.forEach((key) => {
            const value = getQueryValue(key)
            if (value) {
              parsedChips.push({ key, value, title })
            }
          })
        }

        if (item?.props?.dictionary) {
          addQueryChips(item.props.dictionary)
        }
        if (item?.props?.elements) {
          addQueryChips(
            item.props.elements
              .map((element: { dictionary: string }) => element.dictionary)
              .flat(),
          )
        }
        if (item.$cmp === 'VehicleType' && getQueryValue(VEHICLE_TYPE_KEY)) {
          parsedChips.push({
            key: VEHICLE_TYPE_KEY,
            value: getQueryValue(VEHICLE_TYPE_KEY),
            title,
          })
        }
        if (
          item.$cmp === CUSTOM_CHECKBOX_ID
          && getQueryValue(item.props?.name)
        ) {
          parsedChips.push({
            key: item.props.name,
            value: item.props.value ?? true,
            title,
          })
        }

        const value = getQueryValue(item.name)
        if (value) {
          parsedChips.push({ key: item.name, value, title })
        }

        return parsedChips
      })
    })
  })
  const { dictionary } = useDictionary({
    enabled: !!chips.value.length,
  })

  async function filterCurrentValueByType({
    type,
    dictionaryKey,
    storeKey,
  }: FilterCurrentValueByTypeParams) {
    const dictionaryItem = dictionary.value?.[dictionaryKey]

    const filteredIds = new Set(
      dictionaryItem?.options
        ?.filter(item => item.types?.includes(type))
        .map(item => `${item.id}`),
    )

    const currentQueryValue = route.query[storeKey] as
      | string[]
      | string
      | undefined

    const currentValue = Array.isArray(currentQueryValue)
      ? currentQueryValue
      : currentQueryValue
        ? [currentQueryValue]
        : undefined

    const updatedValue = currentValue?.filter(id => filteredIds?.has(id))

    await updateQuery({
      [storeKey]: updatedValue as string[],
    })
  }

  const updateSearchValues = async ({ key, value }: Update) => {
    if (key === VEHICLE_TYPE_KEY && !debounced.value && value) {
      const type
        = VEHICLE_TYPE_TO_CATEGORY_MAP[
          value as keyof typeof VEHICLE_TYPE_TO_CATEGORY_MAP
        ]

      for (const {
        dictionaryKey,
        storeKey,
      } of ITEMS_TO_UPDATE_ON_TYPE_CHANGE) {
        await filterCurrentValueByType({
          storeKey,
          dictionaryKey,
          type,
        })
      }
    }
    const checkIsEmpty = () => {
      if (Array.isArray(value)) {
        return !value.length
      }
      return !value
    }

    if (checkIsEmpty()) {
      await updateQuery({ [key]: '' })
      return
    }

    await updateQuery({ [key]: value as string })
  }

  const reset = () => {
    router.replace({ query: {} })
  }

  return {
    debounced,
    reset,
    chips,
    updateSearchValues,
    initialQuery,
    allDisplayingForms,
  }
})
